import { deviceApi } from "#/api/deviceApi";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import { useClient } from "#/hook/useClient";
import { useGoogleMap } from "#/hook/useGoogleMap";
import { getHardwareLabel } from "#/utils/deviceHardware";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { Accordion } from "@mantine/core";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { IconExternalLink } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import UsageSection from "./components/UsageSection";

export default function ClientDeviceDetailPage() {
  const { clientId, deviceId } = useParams();
  const { client } = useClient();
  const { isLoaded } = useGoogleMap();
  const { t } = useTranslation();

  const { data: device, isLoading } = useQuery({
    queryKey: ["device-detail", deviceId],
    queryFn: async () => {
      const response = await deviceApi.getById(Number(deviceId));
      return response.data;
    },
    enabled: !!deviceId,
  });

  const longitude = device?.longitude && Number(device.longitude) !== 0 ? Number(device.longitude) : (device?.site?.longitude ? Number(device.site.longitude) : null);
  const latitude = device?.latitude && Number(device.latitude) !== 0 ? Number(device.latitude) : (device?.site?.latitude ? Number(device.site.latitude) : null);

  return (
    <DetailPageLayout
      title={device?.name ?? "Device"}
      loading={isLoading}
    >
      <Accordion variant="separated" defaultValue={["device_info", "usage"]} multiple>
        <Accordion.Item value="device_info">
          <Accordion.Control>Device Info</Accordion.Control>
          <Accordion.Panel>
            {device && (
              <div className="w-full grid grid-cols-2">
                <div className="w-full grid grid-cols-1 gap-4 p-4">
                  <div className="flex flex-col">
                    <span className="uppercase text-xs">{t("devices.info.hardware")}</span>
                    <b className="break-all text-sm capitalize">
                      {getHardwareLabel(device.hardware_type)}
                    </b>
                  </div>
                  <div className="flex flex-col">
                    <span className="uppercase text-xs">{t("devices.info.mac")}</span>
                    <b className="break-all text-sm">{device.mac_address}</b>
                  </div>
                  <div className="flex flex-col">
                    <span className="uppercase text-xs">{t("devices.info.serial")}</span>
                    <b className="break-all text-sm">{device.serial_number}</b>
                  </div>
                  <div className="flex flex-col">
                    <span className="uppercase text-xs">{t("devices.info.ip")}</span>
                    <b className="break-all text-sm">{device.ip_address ?? "--"}</b>
                  </div>
                  <div className="flex flex-col">
                    <span className="uppercase text-xs">{t("devices.info.lastActive")}</span>
                    <b className="break-all text-sm">
                      {device.last_seen ? formatDateTime(device.last_seen, client) : "--"}
                    </b>
                  </div>
                  {device.site && (
                    <div className="flex flex-col">
                      <span className="uppercase text-xs">{t("devices.info.site")}</span>
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:underline w-fit"
                        onClick={() => window.open(`/sites/${device.site?.id}`, '_blank')}
                      >
                        <b className="break-all text-sm">{device.site?.name ?? "--"}</b>
                        <IconExternalLink size={14} className="opacity-60" />
                      </div>
                      <p className="break-all text-sm">{device.site?.location_text ?? "--"}</p>
                    </div>
                  )}
                </div>
                {isLoaded && longitude != null && latitude != null && (
                  <div className="p-2 flex flex-col gap-2">
                    <div className="w-full h-full rounded-lg overflow-hidden border-2 border-gray-300">
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={{ lat: latitude, lng: longitude }}
                        zoom={14}
                        options={{ streetViewControl: false, mapTypeControl: false }}
                      >
                        <Marker
                          position={{ lat: latitude, lng: longitude }}
                          title={`MAC: ${device.mac_address}\nIP: ${device.ip_address ?? "--"}\nSerial: ${device.serial_number}`}
                        />
                      </GoogleMap>
                    </div>
                    <span className="text-2xs font-bold mx-auto text-black/70 italic">{t("devices.mapDescription")}</span>
                  </div>
                )}
              </div>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="usage">
          <Accordion.Control>Usage</Accordion.Control>
          <Accordion.Panel>
            {device && (
              <UsageSection
                deviceId={Number(deviceId)}
                clientId={Number(clientId)}
              />
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </DetailPageLayout>
  );
}
